/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { useRouter } from "@tanstack/react-router";

export type PortalExperience = "classic" | "workspace";

export const PORTAL_EXPERIENCE_STORAGE_KEY = "broadview.portal.experience";

function initialExperience(): PortalExperience {
  if (typeof window === "undefined") return "classic";
  const requested = new URLSearchParams(window.location.search).get("experience");
  if (requested === "workspace" || requested === "classic") {
    window.localStorage.setItem(PORTAL_EXPERIENCE_STORAGE_KEY, requested);
    return requested;
  }
  return window.localStorage.getItem(PORTAL_EXPERIENCE_STORAGE_KEY) === "workspace"
    ? "workspace"
    : "classic";
}

type PortalExperienceState = Readonly<{
  experience: PortalExperience;
  setExperience(experience: PortalExperience): void;
}>;

const PortalExperienceContext = createContext<PortalExperienceState | null>(null);

export function PortalExperienceProvider({ children }: Readonly<{ children: ReactNode }>) {
  const router = useRouter();
  const [experience, setExperienceState] = useState<PortalExperience>(initialExperience);
  const value = useMemo<PortalExperienceState>(
    () => ({
      experience,
      setExperience(nextExperience) {
        window.localStorage.setItem(PORTAL_EXPERIENCE_STORAGE_KEY, nextExperience);
        // A shared preview deep link must not override a later explicit choice
        // when reloaded. Keep its route, other query parameters and fragment.
        const url = new URL(window.location.href);
        if (url.searchParams.has("experience")) {
          url.searchParams.set("experience", nextExperience);
          router.history.replace(
            `${url.pathname}${url.search}${url.hash}`,
            router.history.location.state,
          );
        }
        setExperienceState(nextExperience);
      },
    }),
    [experience, router],
  );

  return (
    <PortalExperienceContext.Provider value={value}>{children}</PortalExperienceContext.Provider>
  );
}

export function usePortalExperience(): PortalExperienceState {
  const value = useContext(PortalExperienceContext);
  if (!value) throw new Error("Portal experience is unavailable outside its provider.");
  return value;
}
